import { useCallback, useEffect, useContext, useRef, useState } from 'react'
import { rhybusRListsHttp } from '../api/rhybus-list-http'
import { ListNames, FISCAL_OPTIONS, DEFAULT_FISCAL_YEAR_OPTION, STATUS_ENUM, STATUS_CODE, STATUS_CODE_TRAINIG } from '../config/configuration'
import { loadPublishedSchema } from '../formengine/publish-utils'
import { UserDetailsContext } from '../utils/user-context'
import { UserContext } from '../store/UserContextProvider'
import { getTrainingTrackDetailByGUID } from './listview'
import { structureCaseBlobsForRhyBusFiles } from './detailview'
import { addStatusDiscussionToSharePoint } from './detailview'
import { triggerCaseNotifications, ACTIVITY_ID } from './notificationUtils'

function generateGUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
    })
}

// Mandatory fields for the grant form when no published schema is available
const GRANT_FORM_MANDATORY = [
    { key: 'fiscalYear', label: 'Fiscal Year' },
    { key: 'programType', label: 'Program Type' },
    { key: 'implementingAgency', label: 'IA' },
]

// Mandatory fields for the training track form when no published schema is available
const TRAINING_TRACK_MANDATORY = [
    { key: 'userCaseId', label: 'Case ID' },
    { key: 'line', label: 'Line' },
    { key: 'type', label: 'Type' },
    { key: 'wcn', label: 'WCN' },
    { key: 'priority', label: 'Priority' },
    { key: 'fiscalYear', label: 'Fiscal Year' },
    { key: 'quarter', label: 'Quarter' },
    { key: 'rank', label: 'Rank' },
    { key: 'tpc', label: 'TPC' },
    { key: 'vetted', label: 'Vetted' },
]

// Mandatory fields for each training track line row (keys match TrainingLineRow)
const TRAINING_LINE_MANDATORY = [
    { key: 'suffix', label: 'Suffix' },
    { key: 'tMasl', label: 'T-MASL' },
    { key: 'location', label: 'Location' },
    { key: 'exa', label: 'EXA' },
    { key: 'classId', label: 'Class ID' },
]


// Field types that are structural (headers, buttons) and should not be validated or submitted
const LAYOUT_ONLY_TYPES = new Set(['SectionHeader', 'SubSectionHeader', 'ContractDurationHeader', 'Button'])

const DEFAULT_CASE_TYPE = { label: 'Grants', value: 'Grants' }

const parseLineDate = (dateStr: string | undefined | null): Date | null => {
    if (!dateStr) return null
    const d = new Date(dateStr)
    return isNaN(d.getTime()) ? null : d
}

export function useForm(formType?: string) {
    // REFS
    const formRef = useRef<Record<string, any>>({})

    // CURRENT USER
    const { pageContextdets, roles } = useContext(UserDetailsContext) as any
    const { me } = useContext(UserContext)
    const currentUser = me?.profileName

    // DEFAULT VALUES
    const currentFiscalYear = DEFAULT_FISCAL_YEAR_OPTION

    // STATE
    const [isEditMode, setIsEditMode] = useState(false)
    const [currentStatusCode, setCurrentStatusCode] = useState<number | null>(null)
    const [formHydrationVersion, setFormHydrationVersion] = useState(0)
    const [currentItemKey, setCurrentItemKey] = useState<string | null>(null)
    const [currentItemTag, setCurrentItemTag] = useState<string | null>(null)
    const [currentCaseGUID, setCurrentCaseGUID] = useState<string | null>(null)
    const [currentSubmittedBy, setCurrentSubmittedBy] = useState<string>('')
    const [originalValues, setOriginalValues] = useState<Record<string, any> | null>(null)
    const [currentTrainingTrackGUID, setCurrentTrainingTrackGUID] = useState<string | null>(null)
    const [currentTrainingItemKey, setCurrentTrainingItemKey] = useState<string | null>(null)
    const [currentTrainingItemTag, setCurrentTrainingItemTag] = useState<string | null>(null)
    const [values, setValues] = useState<Record<string, any>>({
        fiscalYear: currentFiscalYear,
        // caseDesignator: String(currentFiscalYear?.value ?? '').replace(/\D/g, '').slice(-2),
        caseType: DEFAULT_CASE_TYPE,
        country: null,
        implementingAgency: null,
        programType: null,
        ceilingUsd: '',
        reservedUsd: '',
        consumedUsd: '',
        releasedUsd: '',
        remainingUsd: '',
        utilizationPercent: '',
        description: '',
        attachments: []
    })

    const [validationMsgs, setValidationMsgs] = useState<any[]>([])
    const [showCancelAlert, setShowCancelAlert] = useState(false)
    const [showInstructions, setShowInstructions] = useState(false)
    const [position, setPosition] = useState<'top' | 'bottom'>('top')

    // HELPERS
    const getNumberValue = (value: any): number => {
        if (value === null || value === undefined || value === '') return 0
        const cleaned = String(value).replace(/[$,]/g, '')
        const parsed = parseFloat(cleaned)
        return Number.isNaN(parsed) ? 0 : parsed
    }

    const selectValue = (value: any) => {
        if (!value) return ''
        if (typeof value === 'object' && 'value' in value) return value.value
        return value
    }

    const selectLabel = (value: any) => {
        if (!value) return ''
        if (typeof value === 'object' && 'label' in value) return value.label
        return selectValue(value)
    }

    const toCodeLabelSelect = (val: string | number | null) => {
        if (val == null || val === '') return null
        const label = String(val)
        const value = label.includes(' - ') ? label.split(' - ')[0] : label
        return { label, value }
    }

    // Returns flat list of all data-entry fields from the published schema for this formType.
    // Called at validation/submit time so it always reflects the latest published state.
    const getSchemaFields = (): any[] | null => {
        if (!formType) return null
        const schema = loadPublishedSchema(formType)
        if (!schema) return null
        return schema.containersData
            .flatMap((c: any) => c.rowData.flatMap((r: any) => r.childData))
            .filter((ch: any) => !LAYOUT_ONLY_TYPES.has(ch.type))
    }

    // ============================================
    // FOCUS FIELD
    // ============================================

    const focusField = (key: string) => {
        // For select fields, data-rb-select is placed on the wrapper div (a real DOM element).
        // Scroll that wrapper into view so the label+control are visible, then focus the
        // inner <input> — react-select opens its dropdown when that input receives focus.
        const wrapper = document.querySelector(`[data-rb-select="${key}"]`) as HTMLElement | null
        if (wrapper) {
            wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' })
            // .rb-select__input is the visible search input inside RhyBusCustomSelect (react-select).
            // Fallback to any non-hidden input so we don't accidentally focus a type="hidden" field.
            const innerInput = (
                wrapper.querySelector<HTMLElement>('.rb-select__input') ??
                wrapper.querySelector<HTMLElement>('input:not([type="hidden"])')
            )
            setTimeout(() => innerInput?.focus(), 300)
            return
        }

        // For text / textarea / date fields the component's id prop maps to the input id.
        // For section containers (e.g. grantLines div), focus the first input inside.
        const byId = document.getElementById(key) as HTMLElement | null
        if (byId) {
            byId.scrollIntoView({ behavior: 'smooth', block: 'center' })
            const isDirectInput = byId instanceof HTMLInputElement || byId instanceof HTMLTextAreaElement
            const focusTarget = isDirectInput
                ? byId
                : byId.querySelector<HTMLElement>('input:not([type="hidden"]), textarea')
            setTimeout(() => focusTarget?.focus(), 300)
            return
        }

        // Last-resort: use the DOM element stored by the component's ref callback.
        const stored = formRef.current[key]
        const storedEl: HTMLElement | null =
            stored instanceof HTMLElement ? stored
                : stored?.el instanceof HTMLElement ? stored.el
                    : null
        if (storedEl) {
            storedEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
            const inner = storedEl.querySelector('input') as HTMLElement | null
                ; (inner ?? storedEl).focus()
        }
    }

    // ============================================
    // CALCULATED FIELDS
    // ============================================

    const calculateFields = (updatedValues: Record<string, any>) => {
        const ceiling = getNumberValue(updatedValues.ceilingUsd)
        const reserved = getNumberValue(updatedValues.reservedUsd)
        const consumed = getNumberValue(updatedValues.consumedUsd)
        const released = getNumberValue(updatedValues.releasedUsd)

        const remaining = ceiling - reserved - consumed - released
        const utilization = ceiling > 0 ? ((consumed / ceiling) * 100).toFixed(2) : '0.00'

        return { remainingUsd: remaining.toFixed(2), utilizationPercent: utilization }
    }

    // ============================================
    // VALIDATION
    // ============================================

    const validate = (grantLines?: GrantLine[], positionValue: 'top' | 'bottom' = 'top') => {
        const errors: any[] = []
        const schemaFields = getSchemaFields()

        if (schemaFields) {
            // Dynamic validation: use published schema's field order and mandatory flags
            schemaFields
                .filter((ch: any) => ch.isMandatory)
                .forEach((ch: any) => {
                    const valueKey = ch.internalName || ch.id
                    const value = values[valueKey]
                    const isEmpty =
                        value === undefined || value === null || value === '' ||
                        (typeof value === 'object' && !Array.isArray(value) && !value?.value) ||
                        (ch.type === 'checkbox' && value === false)
                    if (isEmpty) errors.push({ key: valueKey, text: `${ch.label} is required` })
                })
        } else {
            // Fallback: validate mandatory fields present in the grant form
            GRANT_FORM_MANDATORY.forEach(field => {
                const value = values[field.key]
                const isEmpty =
                    value === undefined || value === null || value === '' ||
                    (typeof value === 'object' && !Array.isArray(value) && !value?.value)
                if (isEmpty) errors.push({ key: field.key, text: `${field.label} is required` })
            })
        }

        if (values.endFiscalYear) {
            const fy = parseInt(selectValue(values.fiscalYear), 10)
            const efy = parseInt(selectValue(values.endFiscalYear), 10)
            if (!isNaN(fy) && !isNaN(efy) && efy < fy) {
                errors.push({ key: 'endFiscalYear', text: 'End Fiscal Year must be greater than or equal to Fiscal Year' })
            }
        }

        // Validate case lines are provided
        if (!grantLines || grantLines.length === 0) {
            errors.push({ key: 'grantLines', text: 'Case Lines are required' })
        }

        setValidationMsgs(errors)
        if (errors.length > 0) {
            if (positionValue === 'bottom') {
                setTimeout(() => {
                    const el = document.getElementById('validation-summary-bottom')
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }, 50)
            } else {
                focusField(errors[0].key)
            }
            return false
        }
        return true
    }
    const validateTrainingTrack = (lines?: TrainingLineRow[]) => {
        const errors: any[] = []
        const schemaFields = getSchemaFields()

        if (schemaFields) {
            schemaFields
                .filter((ch: any) => ch.isMandatory)
                .forEach((ch: any) => {
                    const valueKey = ch.internalName || ch.id
                    const value = values[valueKey]
                    const isEmpty =
                        value === undefined || value === null || value === '' ||
                        (typeof value === 'object' && !Array.isArray(value) && !value?.value) ||
                        (ch.type === 'checkbox' && value === false)
                    if (isEmpty) errors.push({ key: valueKey, text: `${ch.label} is required` })
                })
        } else {
            TRAINING_TRACK_MANDATORY.forEach(field => {
                const value = values[field.key]
                const isEmpty =
                    value === undefined || value === null || value === '' ||
                    (typeof value === 'object' && !Array.isArray(value) && !value?.value)
                if (isEmpty) errors.push({ key: field.key, text: `${field.label} is required` })
            })
            const lineVal = typeof values.line === 'object' ? values.line?.value : values.line
            if (lineVal === 'NA') {
                errors.push({ key: 'line', text: 'Line item is required. The selected Case ID has no lines associated.' })
            }
        }

        if (lines !== undefined) {
            if (lines.length === 0) {
                errors.push({ key: 'trainingLines', text: 'At least one training line is required' })
            } else {
                const sortedLines = [...lines].sort((a, b) => (parseInt(a.training || '0', 10)) - (parseInt(b.training || '0', 10)))
                // Mandatory field check per line
                sortedLines.forEach((line, idx) => {
                    TRAINING_LINE_MANDATORY.forEach(field => {
                        const value = (line as any)[field.key]
                        if (!value) errors.push({ key: 'trainingLines', text: `TrackLine : ${field.label} is required` })
                    })
                })

                // Rule 1: Track FY must be within case FY range
                const trackFY = parseInt(selectValue(values.fiscalYear), 10)
                const caseOption = values.userCaseId && typeof values.userCaseId === 'object' ? values.userCaseId : null
                const caseFYStart = caseOption?.fiscalYear ? parseInt(String(caseOption.fiscalYear), 10) : NaN
                const caseFYEnd = caseOption?.endFiscalYear ? parseInt(String(caseOption.endFiscalYear), 10) : NaN

                if (!isNaN(trackFY) && !isNaN(caseFYStart) && !isNaN(caseFYEnd)) {
                    if (trackFY < caseFYStart || trackFY > caseFYEnd) {
                        errors.push({ key: 'fiscalYear', text: `Track Fiscal Year must be between Case FY ${caseFYStart} and ${caseFYEnd}` })
                    }
                }

                // Precompute case FY date boundaries for rule 3
                const caseFYStartDate = !isNaN(caseFYStart) ? new Date(caseFYStart - 1, 9, 1) : null
                const caseFYEndDate = !isNaN(caseFYEnd) ? new Date(caseFYEnd, 8, 30) : null

                sortedLines.forEach((line, idx) => {
                    const lineNum = idx + 1
                    const startDate = parseLineDate(line.startDate)
                    const endDate = parseLineDate(line.endDate)

                    // Rule 2: If any date provided, all three are required
                    const hasAnyDate = !!(line.reportDate || line.startDate || line.endDate)
                    if (hasAnyDate) {
                        if (!line.reportDate) errors.push({ key: 'trainingLines', text: `TrackLine : Report Date is required when Start or End Date is provided` })
                        if (!line.startDate) errors.push({ key: 'trainingLines', text: `TrackLine : Start Date is required when Report or End Date is provided` })
                        if (!line.endDate) errors.push({ key: 'trainingLines', text: `TrackLine : End Date is required when Report or Start Date is provided` })
                    }

                    // Rule 6: End Date must be on or after Start Date
                    if (startDate && endDate && endDate < startDate) {
                        errors.push({ key: 'trainingLines', text: `TrackLine : End Date must be on or after Start Date` })
                    }

                    // Rules 3 & 4: First line start date range checks
                    if (idx === 0 && startDate) {
                        if (caseFYStartDate && startDate < caseFYStartDate) {
                            errors.push({ key: 'trainingLines', text: `TrackLine : Start Date must be on or after the start of Case Fiscal Year (Oct 1, ${caseFYStart - 1})` })
                        }
                        if (caseFYEndDate && startDate > caseFYEndDate) {
                            errors.push({ key: 'trainingLines', text: `TrackLine : Start Date must be on or before the end of Case End Fiscal Year (Sep 30, ${caseFYEnd})` })
                        }
                    }
                    void lineNum
                })

                // Rule 5: Each line's start date must be after the previous line's end date
                for (let i = 1; i < sortedLines.length; i++) {
                    const prevEnd = parseLineDate(sortedLines[i - 1].endDate)
                    const currStart = parseLineDate(sortedLines[i].startDate)
                    if (prevEnd && currStart && currStart <= prevEnd) {
                        errors.push({ key: 'trainingLines', text: `TrackLine : Start Date must be after Line ${i} End Date` })
                    }
                }
            }
        }

        setValidationMsgs(errors)
        if (errors.length > 0) {
            focusField(errors[0].key)
            return false
        }
        return true
    }

    const validateTrainingTrackDraft = (positionValue: 'top' | 'bottom' = 'top') => {
        const errors: any[] = []
        const isEmptySelect = (value: any) =>
            value === undefined || value === null || value === '' ||
            (typeof value === 'object' && !Array.isArray(value) && !value?.value)

        if (isEmptySelect(values.userCaseId)) {
            errors.push({ key: 'userCaseId', text: 'User Case ID is required to save as draft' })
        }
        if (isEmptySelect(values.line)) {
            errors.push({ key: 'line', text: 'Line is required to save as draft' })
        } else if ((typeof values.line === 'object' ? values.line?.value : values.line) === 'NA') {
            errors.push({ key: 'line', text: 'Line item is required. The selected Case ID has no lines associated.' })
        }

        setPosition(positionValue)
        setValidationMsgs(errors)
        if (errors.length > 0) {
            focusField(errors[0].key)
            return false
        }

        return true
    }

    // ============================================
    // HANDLE CHANGE
    // ============================================

    const buildCaseDesignator = (updatedValues: Record<string, any>): string => {
        const fyLastTwo = String(selectValue(updatedValues.fiscalYear) ?? '').replace(/\D/g, '').slice(-2)
        const ptFirstLetter = String(selectLabel(updatedValues.programType) ?? '').charAt(0).toUpperCase()
        return `${fyLastTwo}${ptFirstLetter}`
    }

    const buildUserCaseId = (updatedValues: Record<string, any>): string => {
        const country = selectValue(updatedValues.country)
        const ia = selectValue(updatedValues.implementingAgency)
        const cd = updatedValues.caseDesignator || ''
        return [country, ia, cd].filter(Boolean).join('-')
    }

    const handleChange = (key: string, value: any) => {
        setValues(prev => {
            const updatedValues = { ...prev, [key]: value }
            if (key === 'fiscalYear') {
                const newStartFY = Number(typeof value === 'object' ? value?.value : value)
                const currentEndFY = Number(typeof prev.endFiscalYear === 'object' ? prev.endFiscalYear?.value : prev.endFiscalYear)
                if (!isNaN(newStartFY) && !isNaN(currentEndFY) && currentEndFY < newStartFY) {
                    updatedValues.endFiscalYear = null
                }
            }
            const calculatedValues = calculateFields(updatedValues)
            const caseDesignator = (key === 'fiscalYear' || key === 'programType')
                ? buildCaseDesignator(updatedValues)
                : updatedValues.caseDesignator
            const valuesWithCd = { ...updatedValues, caseDesignator }
            const userCaseId = formType !== 'training' && (key === 'country' || key === 'implementingAgency' || key === 'caseDesignator' || key === 'fiscalYear' || key === 'programType')
                ? buildUserCaseId(valuesWithCd)
                : prev.userCaseId
            return { ...valuesWithCd, ...calculatedValues, userCaseId }
        })
        formRef.current[key] = value
        setValidationMsgs(prev => prev.filter((m: any) => m.key !== key))
    }

    const handleBlur = (key: string) => {
        if (key === 'caseDesignator') {
            setValues(prev => ({ ...prev, userCaseId: buildUserCaseId(prev) }))
        }
    }

    // ============================================
    // PAYLOAD BUILDER
    // ============================================

    // Maps form values to exactly the DSCACaseMaster columns — nothing extra.
    // Optional Int columns (EndFiscalYear) are omitted when empty to avoid SchemaViolation.
    const buildPayload = (formValues: Record<string, any>, existingCaseGUID?: string | null, statusCode?: number): Record<string, any> => {
        const endFY = parseInt(selectValue(formValues.endFiscalYear), 10)
        const now = new Date().toISOString()
        const payload: Record<string, any> = {
            CaseGUID: existingCaseGUID ?? generateGUID(),
            FiscalYear: parseInt(selectValue(formValues.fiscalYear), 10) || 0,
            ProgramType: selectLabel(formValues.programType) || '',
            FundingSource: selectValue(formValues.fundingSource) || '',
            ImpAgencyID: selectLabel(formValues.implementingAgency) || '',
            Country: selectValue(formValues.country) || '',
            CaseDesignator: formValues.caseDesignator || '',
            UserCaseID: buildUserCaseId(formValues),
            StatusCode: statusCode,
            CaseType: selectValue(formValues.caseType) || 'Grants',
            Description: formValues.description || '',
            ItemCreated: now,
            ItemCreatedBy: currentUser,
            ItemModified: now,
            ItemModifiedBy: currentUser,
        }
        if (!isNaN(endFY) && endFY > 0) {
            payload.EndFiscalYear = endFY
        }
        return payload
    }

    const buildTrainingTrackPayload = (formValues: Record<string, any>, existingTrainingGUID?: string | null, statusCode: number = 1): Record<string, any> => {
        const now = new Date().toISOString()
        const payload: Record<string, any> = {
            TrainingTrackGUID: existingTrainingGUID ?? generateGUID(),
            CaseGUID: selectValue(formValues.userCaseId) || null,
            CaseMasterLineGUID: selectValue(formValues.line) || null,
            WorksheetNumber: selectValue(formValues.wcn) || '',
            TrainingTrackType: selectValue(formValues.type) || '',
            Priority: selectValue(formValues.priority) || '',
            FiscalYear: parseInt(selectValue(formValues.fiscalYear), 10) || 0,
            Rank: selectValue(formValues.rank) || '',
            Quarter: parseInt(selectValue(formValues.quarter), 10) || 0,
            TrainingPersonEmail: formValues.tpc || '',
            TrainingPersonFName: formValues.studentFirstName || '',
            TrainingPersonMName: formValues.studentMiddleName || '',
            TrainingPersonLName: formValues.studentLastName || '',
            Vetted: selectValue(formValues.vetted) === 'true',
            Description: formValues.description || '',
            StatusCode: statusCode,
            ItemCreated: now,
            ItemCreatedBy: currentUser,
            ItemModified: now,
            ItemModifiedBy: currentUser,
        }

        return payload
    }

    const loadTrainingTrackFormByGUID = useCallback(async (itemGuid: string) => {
        try {
            const trainingData = await getTrainingTrackDetailByGUID(itemGuid)
            if (!trainingData) return
            let existingAttachments: any[] = []
            if (trainingData.eKey) {
                try {
                    const blobData = await structureCaseBlobsForRhyBusFiles(trainingData.eKey, 'training-detail-view')
                    existingAttachments = (blobData.files ?? [])
                        .filter((f: any) => f.metadata?.blobContainerName === 'training-form')
                        .map((f: any) => ({
                            ...f,
                            name: f.Name || '',
                        }))
                } catch {
                    // non-fatal: show form without prefilled attachments
                }
            }
            const loadedVals = {
                userCaseId: trainingData.userCaseId ? { label: trainingData.userCaseId, value: trainingData.CaseGUID, } : null,
                line: trainingData.line ? { label: String(trainingData.line), value: trainingData.CaseMasterLineGUID, } : null,
                type: trainingData.type ? { label: trainingData.type, value: trainingData.type, } : null,
                wcn: trainingData.wcn ? { label: trainingData.wcn, value: trainingData.wcn, } : null,
                priority: trainingData.priority ? { label: trainingData.priority, value: trainingData.priority, } : null,
                fiscalYear: trainingData.fiscalYear ? { label: String(trainingData.fiscalYear), value: String(trainingData.fiscalYear), } : null,
                quarter: trainingData.quarter ? { label: String(trainingData.quarter), value: String(trainingData.quarter), } : null,
                rank: trainingData.rank ? { label: trainingData.rank, value: trainingData.rank, } : null,
                tpc: trainingData.tpc || '',
                studentFirstName: trainingData.TrainingPersonFName || '',
                studentMiddleName: trainingData.TrainingPersonMName || '',
                studentLastName: trainingData.TrainingPersonLName || '',
                vetted: { label: trainingData.Vetted ? 'Yes' : 'No', value: trainingData.Vetted ? 'true' : 'false' },
                description: trainingData.description || '',
                attachments: existingAttachments,
            }
            setValues(prev => ({ ...prev, ...loadedVals }))
            setOriginalValues(loadedVals)
            setCurrentTrainingTrackGUID(trainingData.trainingTrackGUID ?? null)
            setCurrentTrainingItemKey(trainingData.eKey ?? null)
            setCurrentTrainingItemTag((trainingData as any).eTag ?? null)
            setCurrentStatusCode(Number(trainingData.StatusCode ?? 0) || null)
            setIsEditMode(Number(trainingData.StatusCode ?? 0) !== 1)
            setFormHydrationVersion(v => v + 1)
            return trainingData.eKey ?? null
        } catch (error) {
            console.error(
                'Failed to load training form:',
                error
            )
            return null
        }
    }, [])

    // ============================================
    // TRAINING TRACK LINES
    // ============================================

    type TrainingLineRow = {
        id?: number | string
        eTag?: string
        training?: string
        suffix?: string
        location?: string
        exa?: string
        priority?: string
        reportDate?: string
        startDate?: string
        endDate?: string
        duration?: string
        status?: string
        financialStatus?: string
        facilityCategory?: string
        classId?: string
        tMasl?: string
    }

    const toISODate = (dateStr: string | undefined | null | any): string | null => {
        if (!dateStr) return null
        const d = typeof dateStr.toDate === 'function' ? dateStr.toDate() : new Date(dateStr)
        if (isNaN(d.getTime())) return null
        // Use UTC noon so the calendar date is the same across all timezones (UTC-11 to UTC+11)
        const year = d.getFullYear()
        const month = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}T12:00:00.000Z`

    }

    const buildTrainingTrackLinePayload = (
        row: TrainingLineRow,
        trainingTrackGUID: string,
        caseMasterLineGUID: string | null,
        statusCode = 2
    ): Record<string, any> => {
        const now = new Date().toISOString()
        const trainingStatus = statusCode === 1
            ? row.status || 'Saved'
            : row.status === 'Saved' ? 'Submitted' : row.status || 'Submitted'
        return {
            TrainingTrackGUID: trainingTrackGUID,
            CaseMasterLineGUID: caseMasterLineGUID,
            WCNSuffix: row.suffix || null,
            TrainingItemID: parseInt(row.training || '1', 10) || 1,
            ESTStudentReportDate: toISODate(row.reportDate),
            ESTStartDate: toISODate(row.startDate),
            ESTEndDate: toISODate(row.endDate),
            TrainingClassID: row.classId || '',
            TMASL: row.tMasl || null,
            Location: row.location || null,
            EXA: row.exa || null,
            TrainingStatus: trainingStatus,
            FinancialStatus: row.financialStatus || 'U',
            FincialCategory: row.facilityCategory || '',
            StatusCode: statusCode,
            Duration: parseInt(row.duration || '0', 10) || 0,
            DurationType: (row.duration || '').replace(/^\d+\s*/, '') || null,
            Priority: row.priority || 'A',
            ItemCreated: now,
            ItemCreatedBy: currentUser,
            ItemModified: now,
            ItemModifiedBy: currentUser,
        }
    }

    const saveTrainingTrackLines = async (
        trainingTrackGUID: string,
        caseMasterLineGUID: string | null,
        lines: TrainingLineRow[],
        statusCode = 2
    ) => {
        if (!lines.length) return
        const lineList = await rhybusRListsHttp().getList(ListNames.DSCA_TRAINING_TRACK_LINE)
        const payloads = lines.map(line =>
            buildTrainingTrackLinePayload(line, trainingTrackGUID, caseMasterLineGUID, statusCode)
        )
        await Promise.all(payloads.map(p => lineList.addOrUpdateItem(p as any)))
    }


    // ============================================
    // GRANT LINES
    // ============================================

    type GrantLine = {
        lineNumber: string
        regionalCenter: string
        eKey?: string
        eTag?: string
        caseMasterLineGUID?: string
    }

    const toLineInt = (lineNumber: string) => {
        const n = parseInt(lineNumber, 10)
        return isNaN(n) ? lineNumber : n
    }

    const addGrantLine = async (caseGUID: string, line: GrantLine) => {
        const lineList = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER_LINE)
        const now = new Date().toISOString()
        await lineList.addOrUpdateItem({
            CaseMasterLineGUID: generateGUID(),
            CaseGUID: caseGUID,
            UserCaseLine: toLineInt(line.lineNumber),
            RegionalCenter: line.regionalCenter,
            IsDeleted: false,
            ItemCreated: now,
            ItemCreatedBy: currentUser,
            ItemModified: now,
            ItemModifiedBy: currentUser,
        } as any)
    }

    const updateGrantLine = async (line: GrantLine) => {
        if (!line.eKey) return
        const lineList = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER_LINE)
        const now = new Date().toISOString()
        await lineList.addOrUpdateItem({
            eKey: line.eKey,
            eTag: line.eTag,
            CaseMasterLineGUID: line.caseMasterLineGUID,
            UserCaseLine: toLineInt(line.lineNumber),
            RegionalCenter: line.regionalCenter,
            ItemModified: now,
            ItemModifiedBy: currentUser,
        } as any)
    }

    const softDeleteGrantLine = async (line: GrantLine) => {
        if (!line.eKey) return
        const lineList = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER_LINE)
        const now = new Date().toISOString()
        await lineList.addOrUpdateItem({
            eKey: line.eKey,
            eTag: line.eTag,
            IsDeleted: true,
            ItemModified: now,
            ItemModifiedBy: currentUser,
        } as any)
    }

    // ============================================
    // HISTORY
    // ============================================

    const saveTrainingHistoryRecord = async (
        trainingTrackGUID: string,
        statusCode: number | null,
        previousStatusCode: number | null,
        action: string
    ) => {
        try {
            const historyList = await rhybusRListsHttp().getList(ListNames.DSCA_TRAINING_HISTORY)
            const now = new Date().toISOString()
            const userRole = Array.isArray(roles) ? (roles as string[]).join(', ') : ''
            await historyList.addOrUpdateItem({
                TrainingTrackGUID: trainingTrackGUID,
                StatusCode: statusCode,
                PreviousStatusCode: previousStatusCode,
                Action: action,
                UserRole: userRole,
                ItemCreated: now,
                ItemCreatedBy: currentUser,
                ItemModified: now,
                ItemModifiedBy: currentUser,
            } as any)
        } catch (err) {
            console.error('[saveTrainingHistoryRecord]', err)
        }
    }

    const saveHistoryRecord = async (
        caseGUID: string,
        caseID: string,
        statusCode: number | null,
        previousStatusCode: number | null,
        action: string
    ) => {
        try {
            const historyList = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_HISTORY)
            const now = new Date().toISOString()
            const userRole = Array.isArray(roles) ? (roles as string[]).join(', ') : ''
            await historyList.addOrUpdateItem({
                CaseGUID: caseGUID,
                CaseID: caseID,
                StatusCode: statusCode,
                PreviousStatusCode: previousStatusCode,
                Action: action,
                UserRole: userRole,
                ItemCreated: now,
                ItemCreatedBy: currentUser,
                ItemModified: now,
                ItemModifiedBy: currentUser,
                createdBy: currentUser,
                createdOn: now,
                modifiedBy: currentUser,
                modifiedOn: now,
            } as any)
        } catch (err) {
            console.error('[saveHistoryRecord]', err)
        }
    }

    // ============================================
    // SAVE
    // ============================================

    const handleSave = async (positionValue: 'top' | 'bottom', grantLines?: GrantLine[], originalGrantLines?: GrantLine[]) => {
        try {
            setPosition(positionValue)
            const payload: Record<string, any> = {
                ...buildPayload(values, currentCaseGUID || undefined, STATUS_CODE.SAVED),
                ...(currentItemKey ? { eKey: currentItemKey } : {}),
                ...(currentItemTag ? { eTag: currentItemTag } : {}),
            }
            const response = await (
                await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER)
            ).addOrUpdateItem(payload as any)
            setCurrentCaseGUID(payload.CaseGUID)
            if ((response as any)?.eKey) setCurrentItemKey((response as any).eKey)
            if ((response as any)?.eTag) setCurrentItemTag((response as any).eTag)
            if (grantLines) {
                const savedLinesArr = originalGrantLines ?? []
                const linesToAdd = grantLines.filter(l => !l.eKey)
                const linesToUpdate = grantLines.filter(l =>
                    l.eKey && savedLinesArr.some(s =>
                        s.eKey === l.eKey && (s.regionalCenter !== l.regionalCenter || s.lineNumber !== l.lineNumber)
                    )
                )
                const linesToDelete = savedLinesArr.filter(
                    s => s.eKey && !grantLines.some(c => c.eKey === s.eKey)
                )
                await Promise.all([
                    ...linesToAdd.map(l => addGrantLine(payload.CaseGUID, l)),
                    ...linesToUpdate.map(l => updateGrantLine(l)),
                    ...linesToDelete.map(l => softDeleteGrantLine(l)),
                ])
            }
            return response
        } catch (err) {
            console.error('[handleSave]', err)
            return false
        }
    }

    // ============================================
    // SUBMIT
    // ============================================

    const handleSubmit = async (positionValue: 'top' | 'bottom', grantLines?: GrantLine[], originalGrantLines?: GrantLine[]) => {
        try {
            setPosition(positionValue)
            // TODO: re-enable validation after backend storage is confirmed working
            const isValid = validate(grantLines, positionValue)
            if (!isValid) return false

            const payload: Record<string, any> = {
                ...buildPayload(values, currentCaseGUID || undefined, STATUS_CODE.WaitingforInternalReview),
                ...(currentItemKey ? { eKey: currentItemKey } : {}),
                ...(currentItemTag ? { eTag: currentItemTag } : {}),
            }

            const response = await (
                await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER)
            ).addOrUpdateItem(payload as any)

            setCurrentCaseGUID(payload.CaseGUID)
            if ((response as any)?.eKey) setCurrentItemKey((response as any).eKey)
            if ((response as any)?.eTag) setCurrentItemTag((response as any).eTag)
            if (grantLines) {
                const savedLinesArr = originalGrantLines ?? []
                const linesToAdd = grantLines.filter(l => !l.eKey)
                const linesToUpdate = grantLines.filter(l =>
                    l.eKey && savedLinesArr.some(s =>
                        s.eKey === l.eKey && (s.regionalCenter !== l.regionalCenter || s.lineNumber !== l.lineNumber)
                    )
                )
                const linesToDelete = savedLinesArr.filter(
                    s => s.eKey && !grantLines.some(c => c.eKey === s.eKey)
                )
                await Promise.all([
                    ...linesToAdd.map(l => addGrantLine(payload.CaseGUID, l)),
                    ...linesToUpdate.map(l => updateGrantLine(l)),
                    ...linesToDelete.map(l => softDeleteGrantLine(l)),
                ])
            }
            await saveHistoryRecord(payload.CaseGUID, buildUserCaseId(values), STATUS_CODE.WaitingforInternalReview, STATUS_CODE.SUBMITTED, 'Submit')
            const userRole = Array.isArray(roles) ? (roles as string[]).join(', ') : ''
            await addStatusDiscussionToSharePoint(payload.CaseGUID, 'Submitted', userRole, { userId: currentUser })
            await triggerCaseNotifications(
                payload.CaseGUID,
                //`Case ${buildUserCaseId(values)} has been submitted by ${currentUser}`,
                `Grant Case ${buildUserCaseId(values)} has been submitted by ${currentUser} and is ready for your review.`,
                ACTIVITY_ID.CaseSubmit,
                STATUS_CODE.WaitingforInternalReview,
                me?.email,
            )
            return { response, caseGUID: payload.CaseGUID, itemKey: (response as any)?.eKey ?? payload.CaseGUID }
        } catch (err) {
            console.error('[handleSubmit]', err)
            return false
        }
    }

    const handleTrainingTrack = async (
        positionValue: 'top' | 'bottom',
        trainingLines: TrainingLineRow[] | undefined,
        statusCode: STATUS_CODE_TRAINIG
    ) => {
        setPosition(positionValue)
        const isUpdate = !!currentTrainingTrackGUID
        const wasPreviouslySubmitted = isUpdate && currentStatusCode !== null && Number(currentStatusCode) !== STATUS_CODE.SAVED
        const trainingTrackGUID = currentTrainingTrackGUID ?? generateGUID()

        const payload = buildTrainingTrackPayload(values, trainingTrackGUID, statusCode)
        const list = await rhybusRListsHttp().getList(ListNames.DSCA_TRAINING_TRACK)
        let response: any = null

        if (isUpdate) {
            // Fetch fresh eTags from the DB and soft-delete existing lines for this GUID.
            const lineList = await rhybusRListsHttp().getList(ListNames.DSCA_TRAINING_TRACK_LINE)
            const allLines = [...(await lineList.query().get<any>())]
            const existingLines = allLines.filter(
                (l: any) => l.TrainingTrackGUID === trainingTrackGUID && !l.IsDeleted
            )
            if (existingLines.length) {
                const now = new Date().toISOString()
                await Promise.all(existingLines.map((l: any) =>
                    lineList.addOrUpdateItem({
                        eKey: l.eKey,
                        ...(l.eTag != null ? { eTag: l.eTag } : {}),
                        IsDeleted: true,
                        ItemModified: now,
                        ItemModifiedBy: currentUser,
                    } as any)
                ))
            }

            // Update parent in-place using stored eKey/eTag — same pattern as DSCA_CASE_MASTER.
            const updatePayload = {
                ...payload,
                ...(currentTrainingItemKey ? { eKey: currentTrainingItemKey } : {}),
                ...(currentTrainingItemTag ? { eTag: currentTrainingItemTag } : {}),
            }
            response = await list.addOrUpdateItem(updatePayload as any)
        } else {
            response = await list.addOrUpdateItem(payload as any)
        }

        if ((response as any)?.eKey) setCurrentTrainingItemKey((response as any).eKey)
        if ((response as any)?.eTag) setCurrentTrainingItemTag((response as any).eTag)
        setCurrentTrainingTrackGUID(trainingTrackGUID)

        if (trainingLines?.length) {
            const caseMasterLineGUID = selectValue(values.line) || null
            await saveTrainingTrackLines(trainingTrackGUID, caseMasterLineGUID, trainingLines, statusCode)
        }

        setCurrentStatusCode(statusCode)
        const action = isUpdate ? 'Update' : 'Submit'
        await saveTrainingHistoryRecord(trainingTrackGUID, statusCode, currentStatusCode, action)
        if (statusCode !== STATUS_CODE_TRAINIG.SAVED) {
            const userRole = Array.isArray(roles) ? (roles as string[]).join(', ') : ''
            const subject = wasPreviouslySubmitted ? 'Updated' : 'Submitted'
            await addStatusDiscussionToSharePoint(trainingTrackGUID, subject, userRole, { userId: currentUser })
            const trainingTrackId = response?.TrainingTrackID
                ? String(response.TrainingTrackID).padStart(5, '0')
                : trainingTrackGUID
            if (!isUpdate) {
                await triggerCaseNotifications(
                    trainingTrackGUID,
                    //`Training ${trainingTrackId} has been submitted by ${currentUser}`,
                    `Training Track ${trainingTrackId} has been submitted by ${currentUser} and is awaiting your seat validation review.`,
                    ACTIVITY_ID.TrainingSubmit,
                    STATUS_CODE_TRAINIG.seatValidation,
                    me?.email,
                )
            }
        }

        return { success: true, guid: trainingTrackGUID, response, itemKey: (response as any)?.eKey ?? currentTrainingItemKey ?? trainingTrackGUID }
    }

    const handleTrainingTrackSave = async (positionValue: 'top' | 'bottom', trainingLines?: TrainingLineRow[], _originalLines?: TrainingLineRow[]) => {
        try {
            return await handleTrainingTrack(positionValue, trainingLines, STATUS_CODE_TRAINIG.SAVED)
        } catch (err: any) {
            console.error('[handleTrainingTrackSave]', err?.message ?? err)
            return { success: false, error: err?.message ?? String(err) }
        }
    }

    const handleTrainingTrackSubmit = async (positionValue: 'top' | 'bottom', trainingLines?: TrainingLineRow[], _originalLines?: TrainingLineRow[]) => {
        setPosition(positionValue)
        const isValid = validateTrainingTrack(trainingLines)
        if (!isValid) return false
        try {
            return await handleTrainingTrack(positionValue, trainingLines, STATUS_CODE_TRAINIG.seatValidation)
        } catch (err: any) {
            console.error('[handleTrainingTrackSubmit]', err?.message ?? err)
            return { success: false, error: err?.message ?? String(err) }
        }
    }

    // ============================================
    // UPDATE
    // ============================================

    const handleUpdate = async (positionValue: 'top' | 'bottom', currentLines?: GrantLine[], savedLines?: GrantLine[]) => {
        try {
            setPosition(positionValue)
            const isValid = validate(currentLines, positionValue)
            if (!isValid) return false

            const payload: Record<string, any> = {
                ...buildPayload(values, currentCaseGUID, currentStatusCode || undefined),
                ...(currentItemKey ? { eKey: currentItemKey } : {}),
                ...(currentItemTag ? { eTag: currentItemTag } : {}),
            }

            const response = await (
                await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER)
            ).addOrUpdateItem(payload as any)

            if ((response as any)?.eKey) setCurrentItemKey((response as any).eKey)
            if ((response as any)?.eTag) setCurrentItemTag((response as any).eTag)

            const currentLinesArr = currentLines ?? []
            const savedLinesArr = savedLines ?? []

            const linesToAdd = currentLinesArr.filter(l => !l.eKey)
            const linesToUpdate = currentLinesArr.filter(l =>
                l.eKey && savedLinesArr.some(s =>
                    s.eKey === l.eKey && (s.regionalCenter !== l.regionalCenter || s.lineNumber !== l.lineNumber)
                )
            )
            const linesToDelete = savedLinesArr.filter(
                s => s.eKey && !currentLinesArr.some(c => c.eKey === s.eKey)
            )

            if (currentCaseGUID && (linesToAdd.length || linesToUpdate.length || linesToDelete.length)) {
                await Promise.all([
                    ...linesToAdd.map(l => addGrantLine(currentCaseGUID, l)),
                    ...linesToUpdate.map(l => updateGrantLine(l)),
                    ...linesToDelete.map(l => softDeleteGrantLine(l)),
                ])
            }
            if (currentCaseGUID) {
                await saveHistoryRecord(currentCaseGUID, buildUserCaseId(values), currentStatusCode, currentStatusCode, 'Update')
                const userRole = Array.isArray(roles) ? (roles as string[]).join(', ') : ''
                await addStatusDiscussionToSharePoint(currentCaseGUID, 'Updated', userRole, { userId: currentUser })
            }
            return response
        } catch (err) {
            console.error('[handleUpdate]', err)
            return false
        }
    }

    // ============================================
    // LOAD FOR EDIT
    // ============================================

    const loadFormByGuid = useCallback(async (guid: string): Promise<{ caseGUID: string | null; itemKey?: string | null }> => {
        try {
            const list = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER)
            const items = [...(await list.query().get<any>())]
            const item = items.find((i: any) => i.CaseGUID === guid || i.eKey === guid)
            if (!item) return { caseGUID: null, itemKey: null }

            const toSelect = (val: string | number | null) =>
                val != null && val !== '' ? { label: String(val), value: String(val) } : null

            setValues(prev => ({
                ...prev,
                fiscalYear: toSelect(item.FiscalYear) ?? prev.fiscalYear,
                endFiscalYear: toSelect(item.EndFiscalYear),
                programType: toCodeLabelSelect(item.ProgramType),
                fundingSource: toSelect(item.FundingSource),
                implementingAgency: toCodeLabelSelect(item.ImpAgencyID),
                country: toSelect(item.Country),
                caseType: toSelect(item.CaseType) ?? DEFAULT_CASE_TYPE,
                caseDesignator: item.CaseDesignator || '',
                userCaseId: item.UserCaseID || '',
                description: item.Description || '',
            }))
            setOriginalValues({
                fiscalYear: toSelect(item.FiscalYear),
                endFiscalYear: toSelect(item.EndFiscalYear),
                programType: toCodeLabelSelect(item.ProgramType),
                fundingSource: toSelect(item.FundingSource),
                implementingAgency: toCodeLabelSelect(item.ImpAgencyID),
                country: toSelect(item.Country),
                caseType: toSelect(item.CaseType),
                caseDesignator: item.CaseDesignator || '',
                description: item.Description || '',
            })
            setCurrentItemKey(item.eKey ?? null)
            setCurrentItemTag(item.eTag ?? null)
            setCurrentCaseGUID(item.CaseGUID ?? null)
            setCurrentStatusCode(item.StatusCode ?? null)
            setIsEditMode(true)
            setFormHydrationVersion(v => v + 1)
            return { caseGUID: item.CaseGUID ?? null, itemKey: item.eKey ?? null }
        } catch (err) {
            console.error('[loadFormByGuid]', err)
            return { caseGUID: null, itemKey: null }
        }
    }, [])

    const loadGrantLinesByGuid = useCallback(async (caseGUID: string): Promise<GrantLine[]> => {
        try {
            const lineList = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER_LINE)
            const all = [...(await lineList.query().get<any>())]
            return all
                .filter((l: any) => l.CaseGUID === caseGUID && !l.IsDeleted)
                .sort((a: any, b: any) => new Date(a.ItemCreated || 0).valueOf() - new Date(b.ItemCreated || 0).valueOf())
                .map((l: any) => ({
                    lineNumber: String(l.UserCaseLine ?? ''),
                    regionalCenter: l.RegionalCenter || '',
                    eKey: l.eKey,
                    eTag: l.eTag,
                    caseMasterLineGUID: l.CaseMasterLineGUID,
                }))
        } catch (err) {
            console.error('[loadGrantLinesByGuid]', err)
            return []
        }
    }, [])

    const prepareForCopy = useCallback(() => {
        setCurrentCaseGUID(null)
        setCurrentItemKey(null)
        setCurrentItemTag(null)
        setCurrentStatusCode(null)
        setIsEditMode(false)
        setOriginalValues(null)
        setCurrentTrainingTrackGUID(null)
        setCurrentTrainingItemKey(null)
        setCurrentTrainingItemTag(null)
    }, [])

    // CHANGE DETECTION
    const hasEditValueChanges = () => {
        const norm = (v: any): string => {
            if (v === null || v === undefined || v === '') return ''
            if (typeof v === 'object' && 'value' in v) return String(v.value ?? '')
            return String(v).trim()
        }
        if (!originalValues) {
            // New form: flag as changed if user has touched any nullable field or description
            const nullableFields = ['endFiscalYear', 'programType', 'fundingSource', 'implementingAgency', 'country']
            if (nullableFields.some(k => norm(values[k]) !== '')) return true
            if ((values.description ?? '').trim() !== '') return true
            if (norm(values.fiscalYear) !== norm(currentFiscalYear)) return true
            return false
        }
        const tracked = ['fiscalYear', 'endFiscalYear', 'programType', 'fundingSource', 'implementingAgency', 'country', 'caseType', 'caseDesignator', 'description']
        return tracked.some(k => norm(values[k]) !== norm(originalValues[k]))
    }

    const hasTrainingTrackValueChanges = () => {
        const norm = (v: any): string => {
            if (v === null || v === undefined || v === '') return ''
            if (typeof v === 'object' && 'value' in v) return String(v.value ?? '')
            return String(v).trim()
        }
        if (!originalValues) {
            // New form: flag as changed if any nullable field has been filled in
            const nullableFields = ['userCaseId', 'line', 'type', 'wcn', 'priority', 'quarter', 'rank', 'vetted', 'tpc', 'studentFirstName', 'studentMiddleName', 'studentLastName']
            if (nullableFields.some(k => norm(values[k]) !== '')) return true
            if ((values.description ?? '').trim() !== '') return true
            if (norm(values.fiscalYear) !== norm(currentFiscalYear)) return true
            return false
        }
        const tracked = ['userCaseId', 'line', 'type', 'wcn', 'priority', 'fiscalYear', 'quarter', 'rank', 'tpc', 'studentFirstName', 'studentMiddleName', 'studentLastName', 'vetted', 'description']
        return tracked.some(k => norm(values[k]) !== norm(originalValues[k]))
    }

    // CANCEL
    const handleCancel = () => {
        // formRef.current = {}
        // setValues({
        //     fiscalYear: currentFiscalYear,
        //     country: null,
        //     implementingAgency: null,
        //     programType: null,
        //     ceilingUsd: '',
        //     reservedUsd: '',
        //     consumedUsd: '',
        //     releasedUsd: '',
        //     remainingUsd: '',
        //     utilizationPercent: '',
        //     description: '',
        //     attachments: []
        // })
        setValidationMsgs([])
        setShowCancelAlert(true)
    }

    // ============================================
    // RETURN
    // ============================================

    const startFYValue = typeof values.fiscalYear === 'object' ? (values.fiscalYear?.value ?? '') : (values.fiscalYear ?? '')
    const endFiscalYearOptions = startFYValue
        ? FISCAL_OPTIONS.filter(o => Number(o.value) >= Number(startFYValue))
        : FISCAL_OPTIONS

    return {
        formRef,
        values,
        setValues,
        validationMsgs,
        setValidationMsgs,
        handleChange,
        handleBlur,
        handleSave,
        handleSubmit,
        handleTrainingTrackSave,
        handleTrainingTrackSubmit,
        validateTrainingTrack,
        validateTrainingTrackDraft,
        handleUpdate,
        handleCancel,
        loadFormByGuid,
        loadTrainingTrackFormByGUID,
        loadGrantLinesByGuid,
        prepareForCopy,
        validate,
        focusField,
        showCancelAlert,
        setShowCancelAlert,
        showInstructions,
        setShowInstructions,
        position,
        setPosition,
        hasEditValueChanges,
        hasTrainingTrackValueChanges,
        isEditMode,
        currentStatusCode,
        currentCaseGUID,
        formHydrationVersion,
        currentGrantLines: currentCaseGUID ? loadGrantLinesByGuid(currentCaseGUID) : [],
        endFiscalYearOptions,
    }
}
